/**
 * Single switch that decides which markets the interface exposes.
 *
 * Change ACTIVE_MARKETS (line 18) and nothing else: exposed UI languages,
 * import portals and country menus all derive from it. No market code is
 * deleted — every entry below stays in place and comes back the moment its
 * country code is added to the list.
 *
 * Backend mirror (SEO / sitemap side): /app/backend/markets.py
 *
 * Example — re-enable Spain:
 *   export const ACTIVE_MARKETS = ["FR", "ES"];
 */

// --------------------------------------------------------------------------
// THE switch. France only for now.
// --------------------------------------------------------------------------
export const ACTIVE_MARKETS = ["FR"];

export const MARKETS = {
  FR: {
    language: "fr",
    hubSlug: "loyer-verite",
    portals: [
      { name: "SeLoger", domain: "seloger.com", example: "https://www.seloger.com/annonces/locations/appartement/paris-11eme-75/…" },
      { name: "Leboncoin", domain: "leboncoin.fr" },
      { name: "Bien'Ici", domain: "bienici.com" },
      { name: "PAP", domain: "pap.fr" },
      { name: "Logic-Immo", domain: "logic-immo.com" },
    ],
  },
  DE: {
    language: "de",
    hubSlug: "mietpreisbremse",
    portals: [
      { name: "ImmoScout24", domain: "immoscout24.de", example: "https://www.immobilienscout24.de/expose/…" },
      { name: "Immowelt", domain: "immowelt.de" },
    ],
  },
  ES: {
    language: "es",
    hubSlug: "precio-alquiler",
    portals: [
      { name: "Idealista", domain: "idealista.com", example: "https://www.idealista.com/inmueble/…" },
      { name: "Fotocasa", domain: "fotocasa.es" },
    ],
  },
  GB: {
    language: "en",
    hubSlug: "uk-deposit-protection",
    portals: [
      { name: "Rightmove", domain: "rightmove.co.uk", example: "https://www.rightmove.co.uk/properties/…" },
      { name: "Zoopla", domain: "zoopla.co.uk" },
    ],
  },
  IT: {
    language: "en",
    hubSlug: "canone-concordato",
    portals: [
      { name: "Immobiliare", domain: "immobiliare.it", example: "https://www.immobiliare.it/annunci/…" },
      { name: "Idealista IT", domain: "idealista.it" },
    ],
  },
  CH: {
    language: "de",
    hubSlug: "anfechtung-anfangsmiete",
    portals: [{ name: "ImmoScout24 CH", domain: "immoscout24.ch" }],
  },
  NL: {
    language: "en",
    hubSlug: "nl-puntensysteem",
    portals: [{ name: "Funda", domain: "funda.nl" }],
  },
  BE: {
    language: "fr",
    hubSlug: "be-loyer-reference",
    portals: [{ name: "Immoweb", domain: "immoweb.be" }],
  },
};

export function isMarketActive(code) {
  return ACTIVE_MARKETS.includes(String(code || "").toUpperCase());
}

/** SEO paths owned by each market (hub + its city pages, extra guides). */
const MARKET_PATHS = {
  FR: ["/loyer-verite"],
  DE: ["/mietpreisbremse", "/mieterselbstauskunft"],
  ES: ["/precio-alquiler"],
  GB: ["/uk-deposit-protection"],
  IT: ["/canone-concordato"],
  CH: ["/anfechtung-anfangsmiete"],
  NL: ["/nl-puntensysteem"],
  BE: ["/be-loyer-reference"],
};

/**
 * True when a path may be indexed: either it belongs to no specific market
 * (home, pricing, legal…) or its market is active. Pages of inactive markets
 * stay reachable but are marked noindex, and are absent from the sitemap.
 */
export function isPathIndexable(path) {
  const p = String(path || "/");
  const owner = Object.keys(MARKET_PATHS).find((code) =>
    MARKET_PATHS[code].some((prefix) => p === prefix || p.startsWith(`${prefix}/`))
  );
  return owner ? isMarketActive(owner) : true;
}

/** Languages exposed in the interface (deduplicated, first market first). */
export const ACTIVE_LANGUAGES = ACTIVE_MARKETS.reduce((acc, code) => {
  const lang = MARKETS[code]?.language;
  if (lang && !acc.includes(lang)) acc.push(lang);
  return acc;
}, []);

/** Portals offered at import, in the order shown to the user. */
export const ACTIVE_PORTALS = ACTIVE_MARKETS.flatMap(
  (code) => MARKETS[code]?.portals || []
);

export const ACTIVE_PORTAL_DOMAINS = ACTIVE_PORTALS.map((p) => p.domain);
export const ACTIVE_PORTAL_NAMES = ACTIVE_PORTALS.map((p) => p.name);

/** Comma-separated portal names, French style: "A, B, C et D". */
export function activePortalsSentence() {
  const names = ACTIVE_PORTAL_NAMES;
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} et ${names[names.length - 1]}`;
}

/** Example listing URL used in the import placeholder. */
export const IMPORT_URL_PLACEHOLDER =
  ACTIVE_PORTALS.find((p) => p.example)?.example ||
  `https://www.${ACTIVE_PORTAL_DOMAINS[0] || "seloger.com"}/…`;

/** Country hub slugs that may be linked from the interface. */
export const ACTIVE_HUB_SLUGS = ACTIVE_MARKETS.map(
  (code) => MARKETS[code]?.hubSlug
).filter(Boolean);
